/**
 * Offers Statistics Tab
 * Uses shared CampaignStatsDashboard with offers-specific data from Voluum.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useMemo } from 'react';

import { useAuth, useFlowOptimizerTags, useOfferPerformanceTags, useOffersWithStats } from '@/hooks';
import { statsTrackerId } from '@/features/trackers';

import {
  CampaignStatsDashboard,
  TruncatedFlowItemCell,
  FILTER_FIELDS_OFFERS,
  GROUP_BY_OPTIONS_OFFERS,
  toRangeISO,
  toGenericStatsRowFromOffer,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

interface OffersStatsTabProps {
  countryKey: string;
  trafficCampaignId?: string | null;
  trafficProvider?: string | null;
  wiserIds: number[];
  workDate: string;
}

export const OffersStatsTab: FC<OffersStatsTabProps> = ({
  countryKey,
  trafficCampaignId,
  trafficProvider,
  wiserIds,
  workDate,
}) => {
  const { user } = useAuth();
  const state = useStatsTabState(workDate, 'offers');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const perfTagsQuery = useOfferPerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsByOfferId = perfTagsQuery.data?.tagsByOfferId ?? {};
  const tagDetailsByOfferId = perfTagsQuery.data?.tagDetailsByOfferId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const flowTagsQuery = useFlowOptimizerTags(
    countryKey,
    trafficCampaignId ?? undefined,
    statsTrackerId(trafficProvider),
    'offers',
    Boolean(trafficCampaignId),
  );
  const flowTagsByOfferId = flowTagsQuery.data?.tagsByItemId ?? {};
  const flowTagColors = flowTagsQuery.data?.tagColors;

  const query = useOffersWithStats({
    countryKey,
    trafficCampaignId: trafficCampaignId ?? undefined,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
  });

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useOffersWithStats({
    countryKey,
    trafficCampaignId: trafficCampaignId ?? undefined,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
    enabled: compare.compareMode,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    (compareQuery.data?.rows ?? []).forEach((r, i) => {
      const g = toGenericStatsRowFromOffer(r, i);
      map.set(g.id, g.voluum);
    });
    return map;
  }, [compareQuery.data?.rows]);

  const allOfferRows = query.data?.rows ?? [];
  const allGenericRows = useMemo(
    () =>
      allOfferRows.map((r, i) => {
        const base = toGenericStatsRowFromOffer(r, i);
        const offerId = r.offerId ? String(r.offerId).trim() : '';
        const tags =
          offerId
            ? (tagsByOfferId[offerId] ??
              Object.entries(tagsByOfferId).find(
                ([k]) => k.toLowerCase() === offerId.toLowerCase(),
              )?.[1]) ?? []
            : [];
        return { ...base, performanceTags: tags };
      }),
    [allOfferRows, tagsByOfferId],
  );

  const { rows, groups } = useFilteredAndSortedRowsGeneric(
    allGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const totals = query.data?.totals ?? {};

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsByOfferId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsByOfferId]);

  const tagDetailsLookup = useCallback(
    (offerId: string) => {
      if (!offerId) return undefined;
      const direct = tagDetailsByOfferId[offerId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByOfferId).find(
        ([k]) => k.toLowerCase() === offerId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByOfferId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      const offerId = row.id;
      const performanceTags = offerId
        ? (tagsByOfferId[offerId] ??
          Object.entries(tagsByOfferId).find(
            ([k]) => k.toLowerCase() === offerId.toLowerCase(),
          )?.[1])
        : undefined;
      const performanceTagDetails = offerId ? tagDetailsLookup(offerId) : undefined;
      return (
        <TruncatedFlowItemCell
          itemId={offerId}
          label={row.label ?? '—'}
          itemType="offer"
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
          flowTags={offerId ? flowTagsByOfferId[offerId] : undefined}
          flowTagColors={flowTagColors}
        />
      );
    },
    [tagsByOfferId, tagDetailsLookup, tagColors, flowTagsByOfferId, flowTagColors],
  );

  return (
    <CampaignStatsDashboard
      tab="offers"
      title="Offers"
      emptyMessage="No offers found for this range."
      loadingTitle="Loading offer performance data"
      loadingDesc="Fetching tracker metrics grouped by offer."
      errorPrefix="Failed to load offer stats."
      firstColumnLabel="Offer"
      firstColumnSortKey="offer"
      rowUnitLabel="offer"
      filterFields={FILTER_FIELDS_OFFERS}
      groupByOptions={GROUP_BY_OPTIONS_OFFERS}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={query.data?.total ?? allGenericRows.length}
      allRowsLength={allGenericRows.length}
      currency={query.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={query.isError ?? false}
      error={query.error}
      isLoading={query.isLoading ?? false}
      isFetching={query.isFetching ?? false}
      dataUpdatedAt={query.dataUpdatedAt}
      refetch={() => query.refetch()}
      showLimitSelect
      showGroupBySelect={false}
      firstColumnClassName="min-w-[280px] md:min-w-[480px]"
      csvExportUserId={user?.id}
      compareCapable
      compareMode={compare.compareMode}
      onToggleCompare={compare.toggleCompareMode}
      compareFromDate={compare.compareFrom}
      compareToDate={compare.compareTo}
      onCompareFromChange={compare.setCompareFrom}
      onCompareToChange={compare.setCompareTo}
      onCompareRangeChange={compare.setCompareRange}
      compareById={compareById}
      compareTotals={compareQuery.data?.totals ?? {}}
      onComparePreset={compare.applyPreset}
      granularity={compare.granularity}
      onGranularityChange={compare.setGranularity}
      countryKey={countryKey}
      wiserIds={wiserIds}
      tagFilterOptions={availableTags}
      tagFilterColors={tagColors}
      tagsLoading={perfTagsQuery.isLoading}
    />
  );
};

export default OffersStatsTab;
